import { useState, useEffect, useMemo } from "react";
import { NavLink, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import OrgSwitcher from "@/components/OrgSwitcher";
import {
  SquaresFour, Sparkle, BookOpen, UsersThree, User, FileText, Receipt, FlowArrow,
  GearSix, SignOut, Crown, Package, Megaphone, UserFocus, Notebook, Storefront,
  Handshake, CaretRight, Broadcast, Lightning, Brain, Users, ChartLineUp, Lifebuoy,
  CurrencyDollar, Lightbulb, Globe,
} from "@phosphor-icons/react";

// ─────────────────────────────────────────────────────────────────────────────
// Phase 1 — Grouped sidebar. This is a PRESENTATION-ONLY reorganization.
// Every URL is unchanged from the previous flat sidebar. Any test-id that was
// used to click into a page is preserved (leads, dashboard, ai-assistant, etc.).
// ─────────────────────────────────────────────────────────────────────────────

// Top-level standalone entry — never inside a group.
const DASHBOARD_ITEM = {
  to: "/dashboard", label: "Dashboard", icon: SquaresFour, enabled: true,
  feature: null, testid: "sidebar-nav-dashboard",
};

// Grouped entries. Groups collapse to a single button; children mirror the old
// flat items 1:1 (same `to`, `feature`, `testid`, `enabled`).
const GROUPS = [
  {
    id: "leads",
    label: "Leads",
    icon: UsersThree,
    testid: "sidebar-group-leads",
    items: [
      { to: "/leads", label: "Leads", icon: UsersThree, enabled: true, feature: "leads", testid: "sidebar-nav-leads" },
      { to: "/leads/recovery", label: "Recovery", icon: Lifebuoy, enabled: true, feature: "leads", testid: "sidebar-nav-leads-recovery" },
      { to: "/nurture-sequences", label: "Nurture Sequences", icon: FlowArrow, enabled: true, feature: "nurture_sequences", testid: "sidebar-nav-nurture-sequences" },
    ],
  },
  {
    id: "customers",
    label: "Customers",
    icon: Users,
    testid: "sidebar-group-customers",
    items: [
      { to: "/customers", label: "Customers", icon: User, enabled: true, feature: "customers", testid: "sidebar-nav-customers" },
      { to: "/partners", label: "Partners", icon: Handshake, enabled: true, feature: "partners", testid: "sidebar-nav-partners" },
    ],
  },
  {
    id: "marketing",
    label: "Marketing",
    icon: Broadcast,
    testid: "sidebar-group-marketing",
    items: [
      { to: "/marketing", label: "Overview", icon: Broadcast, enabled: true, feature: "campaigns", testid: "sidebar-nav-marketing" },
      { to: "/marketing/content", label: "Content Performance", icon: FileText, enabled: true, feature: "campaigns", testid: "sidebar-nav-marketing-content" },
      { to: "/marketing/social", label: "Social Media", icon: ChartLineUp, enabled: true, feature: "campaigns", testid: "sidebar-nav-marketing-social" },
      { to: "/marketing/paid", label: "Paid Advertising", icon: CurrencyDollar, enabled: true, feature: "campaigns", testid: "sidebar-nav-marketing-paid" },
      { to: "/marketing/website-analytics", label: "Website Analytics", icon: Globe, enabled: true, feature: "campaigns", testid: "sidebar-nav-marketing-website-analytics" },
      { to: "/personas", label: "Personas", icon: UserFocus, enabled: true, feature: "personas", testid: "sidebar-nav-personas" },
      { to: "/campaigns", label: "Campaigns", icon: Megaphone, enabled: true, feature: "campaigns", testid: "sidebar-nav-campaigns" },
      { to: "/landing-pages", label: "Landing Pages", icon: Storefront, enabled: true, feature: "landing_pages", testid: "sidebar-nav-landing-pages" },
      { to: "/websites", label: "Websites", icon: Globe, enabled: true, feature: null, testid: "sidebar-nav-websites" },
      { to: "/forms", label: "Forms", icon: Notebook, enabled: true, feature: "forms", testid: "sidebar-nav-forms" },
    ],
  },
  {
    id: "ai-center",
    label: "AI Center",
    icon: Brain,
    testid: "sidebar-group-ai-center",
    items: [
      { to: "/ai-center", label: "Overview", icon: Brain, enabled: true, feature: null, testid: "sidebar-nav-ai-center" },
      { to: "/ai-center/recommendations", label: "Recommendations", icon: Lightbulb, enabled: true, feature: null, testid: "sidebar-nav-recommendations" },
      { to: "/analytics", label: "Executive Analytics", icon: ChartLineUp, enabled: true, feature: null, testid: "sidebar-nav-analytics" },
      { to: "/assistant", label: "AI Assistant", icon: Sparkle, enabled: true, feature: "ai_assistant", testid: "sidebar-nav-ai-assistant" },
    ],
  },
  {
    id: "automation",
    label: "Automation",
    icon: Lightning,
    testid: "sidebar-group-automation",
    items: [
      { to: "/workflows", label: "Workflows", icon: FlowArrow, enabled: true, feature: "workflows", testid: "sidebar-nav-workflows" },
    ],
  },
  {
    id: "knowledge",
    label: "Knowledge",
    icon: BookOpen,
    testid: "sidebar-group-knowledge",
    items: [
      { to: "/knowledge-base", label: "Knowledge Base", icon: BookOpen, enabled: true, feature: "knowledge_base", testid: "sidebar-nav-knowledge-base" },
    ],
  },
  {
    id: "settings",
    label: "Settings",
    icon: GearSix,
    testid: "sidebar-group-settings",
    items: [
      { to: "/products", label: "Products", icon: Package, enabled: true, feature: "products", testid: "sidebar-nav-products" },
      { to: "/settings", label: "Settings", icon: GearSix, enabled: true, feature: null, testid: "sidebar-nav-settings" },
      { to: "/quotations", label: "Quotations", icon: FileText, enabled: false, feature: null, testid: "sidebar-nav-quotations" },
      { to: "/invoices", label: "Invoices", icon: Receipt, enabled: false, feature: null, testid: "sidebar-nav-invoices" },
    ],
  },
];

const OPEN_STATE_KEY = "apexgrey.sidebar.groupsOpen.v1";

function loadOpenState() {
  try {
    const raw = localStorage.getItem(OPEN_STATE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === "object" ? parsed : null;
  } catch { return null; }
}

function saveOpenState(state) {
  try { localStorage.setItem(OPEN_STATE_KEY, JSON.stringify(state)); } catch { /* ignore */ }
}

function itemVisible(item, features) {
  // Hide items whose feature flag is explicitly off for this org.
  if (item.feature && features[item.feature] === false) return "off";
  if (!item.enabled) return "soon";
  return "ok";
}

function LeafItem({ item }) {
  const Icon = item.icon;
  return (
    <NavLink
      to={item.to}
      data-testid={item.testid}
      className={({ isActive }) =>
        `group flex items-center gap-3 pl-9 pr-2.5 py-1.5 rounded-md text-[13px] transition-colors ${
          isActive
            ? "bg-white/10 text-ink-primary font-medium"
            : "text-ink-secondary hover:text-ink-primary hover:bg-white/5"
        }`
      }
    >
      <Icon weight="duotone" className="h-3.5 w-3.5 opacity-80" />
      <span className="truncate">{item.label}</span>
    </NavLink>
  );
}

function LeafInactive({ item, state }) {
  const Icon = item.icon;
  return (
    <div
      data-testid={item.testid}
      data-feature-state={state === "off" ? "off" : "soon"}
      className="flex items-center gap-3 pl-9 pr-2.5 py-1.5 rounded-md text-[13px] text-ink-secondary/60 cursor-not-allowed select-none"
      aria-disabled="true"
      title={state === "off" ? "Disabled for this workspace" : "Coming soon"}
    >
      <Icon weight="duotone" className="h-3.5 w-3.5 opacity-60" />
      <span className="flex-1 truncate">{item.label}</span>
      <span className="text-[9px] uppercase tracking-widest text-ink-secondary/50 border border-white/10 px-1.5 py-0.5 rounded-sm">
        {state === "off" ? "Off" : "Soon"}
      </span>
    </div>
  );
}

function Group({ group, features, isOpen, onToggle, currentPath }) {
  const Icon = group.icon;
  // Compute visible items — a group with zero visible items is skipped by the parent.
  const visibleItems = group.items
    .map((it) => ({ ...it, _state: itemVisible(it, features) }));
  const activeChildTo = visibleItems.find((it) =>
    it._state === "ok" && (currentPath === it.to || currentPath.startsWith(it.to + "/"))
  )?.to;
  const groupHasActive = !!activeChildTo;

  return (
    <li>
      <button
        type="button"
        data-testid={group.testid}
        data-open={isOpen ? "true" : "false"}
        aria-expanded={isOpen}
        onClick={onToggle}
        className={`w-full flex items-center gap-3 px-2.5 py-2 rounded-md text-sm transition-colors ${
          groupHasActive
            ? "text-ink-primary bg-white/[0.03]"
            : "text-ink-secondary hover:text-ink-primary hover:bg-white/5"
        }`}
      >
        <Icon weight="duotone" className="h-4 w-4" />
        <span className="flex-1 text-left">{group.label}</span>
        <CaretRight
          weight="bold"
          className={`h-3 w-3 opacity-60 transition-transform ${isOpen ? "rotate-90" : ""}`}
        />
      </button>
      {isOpen && (
        <ul
          className="mt-1 mb-1 space-y-0.5 border-l border-white/5 ml-4"
          data-testid={`${group.testid}-items`}
        >
          {visibleItems.map((item) => (
            <li key={item.to || item.label}>
              {item._state === "ok"
                ? <LeafItem item={item} />
                : <LeafInactive item={item} state={item._state} />}
            </li>
          ))}
        </ul>
      )}
    </li>
  );
}

export default function Sidebar() {
  const { user, currentOrg, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const features = currentOrg?.enabled_features || {};
  const isPlatformAdmin = !!user?.is_platform_admin;

  // Which group contains the currently-active route? That group must open on load.
  const activeGroupId = useMemo(() => {
    for (const g of GROUPS) {
      if (g.items.some((it) => it.enabled && (location.pathname === it.to || location.pathname.startsWith(it.to + "/")))) {
        return g.id;
      }
    }
    return null;
  }, [location.pathname]);

  const [openMap, setOpenMap] = useState(() => {
    const persisted = loadOpenState() || {};
    // Default: everything closed EXCEPT the group whose child is the current route.
    const base = {};
    for (const g of GROUPS) base[g.id] = persisted[g.id] ?? false;
    return base;
  });

  // Whenever route changes to a group's child, ensure that group is open. Don't
  // auto-close others — respect the user's explicit toggle history.
  useEffect(() => {
    if (activeGroupId && !openMap[activeGroupId]) {
      setOpenMap((prev) => {
        const next = { ...prev, [activeGroupId]: true };
        saveOpenState(next);
        return next;
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeGroupId]);

  const toggleGroup = (gid) => {
    setOpenMap((prev) => {
      const next = { ...prev, [gid]: !prev[gid] };
      saveOpenState(next);
      return next;
    });
  };

  const handleLogout = async () => {
    await logout();
    navigate("/login", { replace: true });
  };

  // Determine whether Dashboard is active
  const dashboardActive = location.pathname === "/dashboard";

  return (
    <aside
      data-testid="sidebar"
      className="h-screen w-64 shrink-0 border-r border-white/10 bg-graphite-950 flex flex-col"
    >
      {/* Brand */}
      <div className="px-5 pt-5 pb-3 flex items-center gap-3">
        <div className="h-7 w-7 rounded-sm bg-white text-graphite-950 grid place-items-center font-display font-semibold text-sm">A</div>
        <div className="font-display tracking-tight text-[15px]">ApexGrey</div>
        <span className="ml-auto text-[10px] uppercase tracking-widest text-ink-secondary/70">v0.1</span>
      </div>

      {/* Org switcher */}
      <div className="px-3 pb-3">
        <OrgSwitcher />
      </div>

      <div className="mx-3 my-2 h-px bg-white/10" />

      {/* Nav */}
      <nav className="flex-1 px-3 overflow-y-auto" data-testid="sidebar-nav">
        <div className="text-[10px] uppercase tracking-[0.2em] text-ink-secondary/70 px-2 mb-2 mt-2">Navigate</div>
        <ul className="space-y-1">
          {/* Dashboard — standalone */}
          <li>
            <NavLink
              to={DASHBOARD_ITEM.to}
              data-testid={DASHBOARD_ITEM.testid}
              className={`group flex items-center gap-3 px-2.5 py-2 rounded-md text-sm transition-colors ${
                dashboardActive
                  ? "bg-white/10 text-ink-primary font-medium"
                  : "text-ink-secondary hover:text-ink-primary hover:bg-white/5"
              }`}
            >
              <SquaresFour weight="duotone" className="h-4 w-4" />
              <span>{DASHBOARD_ITEM.label}</span>
            </NavLink>
          </li>
          {/* Grouped entries */}
          {GROUPS.map((g) => {
            // If EVERY item in the group is hidden (all features off + no "soon"
            // items to advertise), skip rendering the group entirely.
            const anyRenderable = g.items.some((it) => {
              const s = itemVisible(it, features);
              return s === "ok" || s === "soon";
            });
            if (!anyRenderable) return null;
            return (
              <Group
                key={g.id}
                group={g}
                features={features}
                isOpen={!!openMap[g.id]}
                onToggle={() => toggleGroup(g.id)}
                currentPath={location.pathname}
              />
            );
          })}
        </ul>

        {isPlatformAdmin && (
          <>
            <div className="text-[10px] uppercase tracking-[0.2em] text-amber-300/80 px-2 mb-2 mt-6 flex items-center gap-1.5">
              <Crown weight="fill" className="h-3 w-3" />
              Platform
            </div>
            <ul className="space-y-1">
              <li>
                <NavLink
                  to="/platform"
                  data-testid="sidebar-nav-platform"
                  className={({ isActive }) =>
                    `group flex items-center gap-3 px-2.5 py-2 rounded-md text-sm transition-colors ${
                      isActive
                        ? "bg-amber-400/10 text-amber-200 border border-amber-400/20"
                        : "text-amber-200/80 hover:text-amber-100 hover:bg-amber-400/5"
                    }`
                  }
                >
                  <Crown weight="duotone" className="h-4 w-4" />
                  <span>Platform Admin</span>
                </NavLink>
              </li>
            </ul>
          </>
        )}
      </nav>

      {/* User */}
      <div className="border-t border-white/10 p-3">
        <NavLink
          to="/profile"
          data-testid="sidebar-profile-link"
          className={({ isActive }) =>
            `flex items-center gap-3 rounded-md px-2 py-2 transition-colors ${
              isActive ? "bg-white/10" : "hover:bg-white/5"
            }`
          }
        >
          {user?.picture ? (
            <img src={user.picture} alt="" className="h-8 w-8 rounded-full object-cover border border-white/10" />
          ) : (
            <div className="h-8 w-8 rounded-full bg-graphite-700 grid place-items-center text-sm">
              {(user?.name || user?.email || "U").charAt(0).toUpperCase()}
            </div>
          )}
          <div className="min-w-0 flex-1">
            <div className="truncate text-sm text-ink-primary" data-testid="sidebar-user-name">
              {user?.name || "Signed in"}
            </div>
            <div className="truncate text-[11px] text-ink-secondary" data-testid="sidebar-user-email">
              {user?.email}
            </div>
          </div>
        </NavLink>
        <button
          data-testid="sidebar-logout-button"
          onClick={handleLogout}
          className="mt-2 w-full inline-flex items-center gap-2 rounded-md px-2 py-2 text-sm text-ink-secondary hover:text-ink-primary hover:bg-white/5 transition-colors"
        >
          <SignOut weight="bold" className="h-4 w-4" />
          Sign out
        </button>
      </div>
    </aside>
  );
}
